import { getTranslationManualBaseFee } from "./baseFeeCalculators";

export const FEE_FIELD_MAP = {
  monitoring: {
    label: "Monitoring",
    isMultiTargetsCountry: true,
    baseFee: 250,
    hasAutoValue: true,
  },
  scripting: {
    label: "Project management",
    hasAutoValue: true,
  },
  translation: {
    label: "Translation",
    isMultiTargetsCountry: true,
    baseFee: getTranslationManualBaseFee,
    hideOnNull: true,
  },
  design_questionnaire: {
    label: "Questionnaire design",
    baseFee: 1000,
  },
  analysis: {
    label: "Analysis",
    baseFee: 5000,
  },
  other: {
    label: "Other",
    baseFee: 100,
  },
};

const getNum = (val) => {
  const n = parseFloat(val);
  return isNaN(n) ? 0 : n;
};

const hasNumberLikeValue = (val) => val !== null && val !== undefined && val !== "" && !Number.isNaN(parseFloat(val));

export const getManualField = (feeKey) => `${feeKey}_manual`;
export const getAutoField = (feeKey) => `${feeKey}_auto`;

export const getActiveTargets = (project) => (project.targets || []).filter((target) => target.active);

const formatCurrencyGeneric = (val, { ceil = false } = {}) => {
  if (val == null) return "-";
  const num = parseFloat(val);
  if (ceil) return `${Math.ceil(num).toLocaleString()} €`;
  return `${num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
};

export const formatCurrency = (val) => formatCurrencyGeneric(val);
export const formatCPI = (val) => formatCurrencyGeneric(val);
export const formatCurrencyCeil = (val) => formatCurrencyGeneric(val, { ceil: true });
export const formatInteger = (val) => (val == null ? "-" : val.toLocaleString());

export const getValueOrDefault = (manual, auto) => {
  const m = getNum(manual);
  return m > 0 ? m : getNum(auto);
};

export const getFeeValueOrDefault = (manual, auto) => {
  return hasNumberLikeValue(manual) ? getNum(manual) : getNum(auto);
};

export const getCountryValue = (country, manualField, autoField) => {
  return getValueOrDefault(country[manualField], autoField ? country[autoField] : undefined);
};

export const calculateCountryTotal = (country) => {
  const n = getNum(country.n);
  const cpi = getCountryValue(country, "cpi_manual", "cpi_auto");
  const monitoring = getFeeValueOrDefault(country.monitoring_manual, country.monitoring_auto);
  const translation = getNum(country.translation_manual);
  return n * cpi + monitoring + translation;
};

const aggregateCountries = (project, reducer) => {
  const callback = typeof reducer === "function" ? reducer : (acc, country) => acc + getNum(country[reducer]);

  return getActiveTargets(project).reduce((sum, target) => {
    return sum + (target.countries || []).reduce(callback, 0);
  }, 0);
};

const calculateFee = (project, feeKey, source = "both") => {
  const config = FEE_FIELD_MAP[feeKey];
  if (!config) return 0;

  const manualField = getManualField(feeKey);
  const autoField = config.hasAutoValue ? getAutoField(feeKey) : null;

  if (config.isMultiTargetsCountry) {
    if (source === "manual") return aggregateCountries(project, manualField);
    if (source === "auto") return autoField ? aggregateCountries(project, autoField) : 0;
    return aggregateCountries(
      project,
      (acc, country) => acc + getFeeValueOrDefault(country[manualField], autoField ? country[autoField] : undefined),
    );
  }

  if (source === "manual") return getNum(project[manualField]);
  if (source === "auto") return autoField ? getNum(project[autoField]) : 0;
  return getFeeValueOrDefault(project[manualField], autoField ? project[autoField] : undefined);
};

export const calculateFeeTotal = (project, feeKey) => calculateFee(project, feeKey, "both");
export const calculateFeeManualTotal = (project, feeKey) => calculateFee(project, feeKey, "manual");
export const calculateFeeAutoTotal = (project, feeKey) => calculateFee(project, feeKey, "auto");

export const hasFeeManualValue = (project, feeKey) => {
  const config = FEE_FIELD_MAP[feeKey];
  if (!config) return false;

  const manualField = getManualField(feeKey);

  if (config.isMultiTargetsCountry) {
    return getActiveTargets(project).some((target) =>
      (target.countries || []).some((country) => hasNumberLikeValue(country[manualField])),
    );
  }

  return hasNumberLikeValue(project[manualField]);
};

export const calculateCountryTotalDisplay = (country) => {
  const cpi = getCountryValue(country, "cpi_manual", "cpi_auto");
  const n = getNum(country.n);
  return Math.round(cpi * n).toLocaleString();
};

export const getServiceFees = (project, visibilityOverrides = {}) => {
  return Object.entries(FEE_FIELD_MAP).map(([type, config]) => ({
    type,
    label: config.label,
    show: visibilityOverrides[type] ?? (hasFeeManualValue(project, type) || calculateFeeTotal(project, type) > 0),
    isMultiTargetsCountry: config.isMultiTargetsCountry,
  }));
};

export const buildInitialFeeVisibility = (project) =>
  Object.fromEntries(getServiceFees(project).map((fee) => [fee.type, fee.show]));

export const calculateProjectTotals = (project) => {
  let n = 0;
  let cpiSum = 0;
  let cpiCount = 0;
  let countriesTotal = 0;

  getActiveTargets(project).forEach((target) => {
    (target.countries || []).forEach((country) => {
      const cpi = getCountryValue(country, "cpi_manual", "cpi_auto");
      const countryN = parseFloat(country.n) || 0;

      if (cpi > 0) {
        cpiSum += cpi;
        cpiCount++;
      }
      n += countryN;
      countriesTotal += calculateCountryTotal(country);
    });
  });

  const projectLevelFees = Object.keys(FEE_FIELD_MAP)
    .filter((type) => !FEE_FIELD_MAP[type].isMultiTargetsCountry)
    .reduce((sum, type) => sum + calculateFeeTotal(project, type), 0);

  return {
    n,
    cpiAvg: cpiCount > 0 ? (cpiSum / cpiCount).toFixed(2) : "0",
    monitoring: calculateFeeTotal(project, "monitoring"),
    translation: calculateFeeTotal(project, "translation"),
    countriesTotal,
    projectLevelFees,
    total: countriesTotal + projectLevelFees,
  };
};
